import React from "react";
import PropTypes from "prop-types";
import { Checkbox } from "primereact/checkbox";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  STATUS_LABELS,
  STATUS_IDS,
  ACTION_IDS,
  statusIsEditable,
  TableEmptyMessage,
  notifyError,
} from "../../../../components/Utils";
import { DeviceFormMenu } from "../../../../components/Buttons";
import ItemListManager from "../../../../components/ItemListManager";
import ADConnectionForm, { WorkplaceRadioGroup } from "./ADConnectionForm";
import {
  PermissionResource,
  PermissionSection,
  Resources,
} from "../../../Permissions";
import { InputText } from "../../../../components/Inputs";

function hasVodafoneAccount(model = {}) {
  return model.adConnection && model.adConnection.toLowerCase() === "ja";
}

function ADConnectionListForm({
  forwardedRef,
  id = "vodafoneAccountListForm",
  className = "",
  model = {
    adConnection: "",
    adUsername: "",
    adPassword: "",
    adCellNumber: "",
    vf_email: "",
    ngumUserId: "",
    workplace: "",
    workplaceData: {
      name: "",
      status_id: 0,
    },
  },
  onChange,
  disabled: isDisabled,
  readOnly = false,
  vfEmailPermission,
  ngumUserIdPermission,
}) {
  const formRef = React.useRef(null);
  const ngumUserIdRef = React.useRef(null);
  const disabled = React.useMemo(
    () => isDisabled || readOnly,
    [isDisabled, readOnly],
  );

  const generateState = React.useCallback(() => {
    const data = { ...(model || {}) };
    if (!data.workplace) {
      data.workplace = "";
      data.workplaceData = {
        name: "",
        status_id: 0,
      };
    }
    const exists = hasVodafoneAccount(model);
    return {
      items: (exists ? [data] : []).map((item, index) => ({
        ...item,
        index,
      })),
      first: 0,
    };
  }, [model]);

  const [state, setState] = React.useState(generateState());

  const getCurrentModel = React.useCallback(() => {
    const { items } = state;
    if (items.length === 0) {
      return model;
    }
    const { index, ...model } = items[0];
    const pModel = model || {};
    model.ad_id = pModel.ad_id;
    return model;
  }, [state, model]);

  const validate = React.useCallback(() => {
    const newModel = getCurrentModel();
    // validate workplace if vodafone account is present
    if (hasVodafoneAccount(model)) {
      if (
        (!newModel.workplace || newModel.workplace === "") &&
        model.workplace
      ) {
        // invalid workplace
        notifyError({
          summary: "Vodafone Account Fehler",
          detail: "Bitte wählen Sie einen Arbeitsort aus.",
        });
        return null;
      }
      if (model.workplace && model.workplace !== newModel.workplace) {
        newModel.workplaceData.action_id = ACTION_IDS.UPDATE;
        newModel.workplaceData.status_id = STATUS_IDS.UPDATING;
      }
    }
    if (newModel) {
      if (ngumUserIdRef.current) {
        newModel.ngumUserId = ngumUserIdRef.current.getValue();
      } else {
        newModel.ngumUserId = "";
      }
    }
    return newModel;
  }, [getCurrentModel, model]);

  const getModel = React.useCallback(() => validate(), [validate]);
  React.useImperativeHandle(forwardedRef, () => ({ getModel }));

  const onSave = React.useCallback(
    (onHide, updating) => {
      const newModel = formRef.current.getModel();
      if (!newModel) return;
      newModel.adStatus_id = updating ? STATUS_IDS.UPDATING : STATUS_IDS.NEW;
      newModel.adAction_id = updating ? ACTION_IDS.UPDATE : ACTION_IDS.CREATE;
      const { items } = state;
      if (newModel.index !== undefined && items[newModel.index] !== undefined) {
        items[newModel.index] = newModel;
      } else {
        newModel.index = items.length;
        items.push(newModel);
      }
      setState((prev) => ({ ...prev, items }));
      onChange && onChange(newModel.adConnection, newModel.adStatus_id);
      onHide && onHide();
    },
    [state, onChange],
  );

  const onDelete = React.useCallback(
    (selected) => {
      if (selected.id) {
        const { items } = state;
        if (items[selected.index]) {
          items[selected.index].adStatus_id = STATUS_IDS.DELETING;
          items[selected.index].adAction_id = ACTION_IDS.DELETE;
          items[selected.index].adStatus = null;
          setState((prev) => ({ ...prev, items }));
          const { adConnection, adStatus_id } = items[selected.index];
          onChange && onChange(adConnection, adStatus_id);
        } else {
          console.error(
            "<< vodafone connection to be deleted was not found",
            this.state.items,
          );
        }
      } else {
        const items = [];
        for (const item of state.items) {
          if (item.index !== selected.index) {
            items.push(item);
          }
        }
        setState((prev) => ({ ...prev, items }));
        onChange && onChange(null);
      }
    },
    [state, onChange],
  );

  const viewVFEmail = React.useMemo(
    () => vfEmailPermission && vfEmailPermission.read,
    [vfEmailPermission],
  );
  const editVFEmail = React.useMemo(
    () => vfEmailPermission && vfEmailPermission.write,
    [vfEmailPermission],
  );
  const viewNGUMUserId = React.useMemo(
    () => ngumUserIdPermission && ngumUserIdPermission.read,
    [ngumUserIdPermission],
  );
  const editNGUMUserId = React.useMemo(
    () => ngumUserIdPermission && ngumUserIdPermission.write,
    [ngumUserIdPermission],
  );

  const onWorkplaceChange = React.useCallback(
    (value) => {
      const { items } = state;
      if (items.length > 0) {
        items[0].workplace = value;
        setState((prev) => ({ ...prev, items }));
      }
    },
    [state],
  );

  const markupNGumUserId = React.useMemo(() => {
    if (!viewNGUMUserId || state.items.length === 0) {
      return null;
    }
    return (
      <NGumUserIDTextField
        ref={ngumUserIdRef}
        data={state.items[0]}
        readOnly={!editNGUMUserId}
      />
    );
  }, [viewNGUMUserId, editNGUMUserId, state]);

  const renderItemTable = React.useCallback(
    (items, actionTemplate) => {
      const data = items.map((item, index) => {
        const status_id = item.adStatus_id || STATUS_IDS.NEW;
        return {
          ...item,
          index,
          vf_email: status_id === STATUS_IDS.NEW ? "" : item.vf_email,
          adStatus: item.adStatus || STATUS_LABELS[status_id],
        };
      });
      return (
        <>
          <DataTable
            value={data}
            rows={1}
            emptyMessage={
              <TableEmptyMessage
                prefix="Kein"
                itemNamePlural="Vodafone Account"
                min
              />
            }
          >
            {viewVFEmail && (
              <Column field="vf_email" header="Vodafone E-Mail" />
            )}
            <Column field="adUsername" header="Kennung" />
            {viewNGUMUserId && (
              <Column
                field="ngumUserId"
                header="nGUM User ID"
                body={() => markupNGumUserId}
              />
            )}
            <Column field="adStatus" header="Status" />
            <Column
              body={actionTemplate}
              style={{ textAlign: "center", width: "8em" }}
            />
          </DataTable>
          <ADConnectionBottomForm
            model={state.items.length ? state.items[0] : {}}
            onChange={onWorkplaceChange}
          />
        </>
      );
    },
    [state, viewVFEmail, viewNGUMUserId, markupNGumUserId],
  );

  const renderItemForm = React.useCallback(
    (item, onHide) => {
      const saveDisabled = item && item.status_id !== STATUS_IDS.NEW;
      return (
        <>
          <ADConnectionForm
            ref={formRef}
            model={item}
            vfEmailReadable={viewVFEmail}
            vfEmailEditable={editVFEmail}
            ngumUserIdReadable={viewNGUMUserId}
            ngumUserIdEditable={editNGUMUserId}
            key={Math.random()}
          />
          <DeviceFormMenu
            className="margin-big-top w3-border-top pad-big w3-light-grey"
            style={{
              marginLeft: "-1em",
              marginRight: "-1em",
              marginBottom: "-1em",
            }}
            updateHidden
            onSave={() => onSave(onHide)}
            saveDisabled={saveDisabled}
            saveHidden={disabled || saveDisabled}
            saveBtnLabel="Beantragen"
            saveBtnName="saveVodafoneAccBtn"
            onCancel={onHide}
            cancelBtnLabel={disabled ? "Schließen" : undefined}
            cancelBtnName="cancelVodafoneAccBtn"
          />
        </>
      );
    },
    [
      onSave,
      viewVFEmail,
      editVFEmail,
      viewNGUMUserId,
      editNGUMUserId,
      disabled,
    ],
  );

  const getItemFormTitle = React.useCallback((connection) => {
    let title = "Vodafone Account bearbeiten";
    if (!connection) title = "Vodafone Account anlegen";
    return <h4 className="no-margin">{title}</h4>;
  }, []);

  const isEditDisabled = (item) => true || !!item.ad_id || disabled;
  // const isDeleteDisabled = (item) =>(
  //   (item.id === undefined || item.id === null) || !statusIsEditable(item.adStatus_id, item.ad_id) || disabled
  // );
  const isDeleteDisabled = (item) => {
    if (item.id === undefined || item.id === null) {
      return false;
    }
    return !statusIsEditable(item.adStatus_id, item.ad_id) || disabled;
  };

  return (
    <ItemListManager
      id={id}
      key={Math.random()}
      itemPanelStyle={{ width: "480px" }}
      className={className}
      title={
        <h4 className="no-margin-top" style={{ height: "36px" }}>
          {"Vodafone Account"}
        </h4>
      }
      addBtnLabel="Hinzufügen"
      addBtnName="addAdConnectionBtn"
      addBtnDisabled={disabled}
      addBtnHidden={state.items.length === 1}
      items={state.items}
      getItemFormTitle={getItemFormTitle}
      renderItemTable={renderItemTable}
      renderItemForm={renderItemForm}
      namePrefix="adConnection"
      isEditDisabled={isEditDisabled}
      hideDisabledEdit
      itemDeleteable={!disabled}
      hideDisabledDelete
      isDeleteDisabled={isDeleteDisabled}
      deleteTitle="Vodafone Account löschen"
      getDeleteText={(item) => {
        if (item.id === undefined || item.id === null) {
          return `Wollen Sie wirklich löschen?`;
        }
        return `Wollen Sie eine Löschung des Vodafone Accounts wirklich beantragen?`;
      }}
      onDelete={onDelete}
    />
  );
}

ADConnectionListForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.shape({
    adConnection: PropTypes.string,
    adUsername: PropTypes.string,
    adPassword: PropTypes.string,
    adCellNumber: PropTypes.string,
    vf_email: PropTypes.string,
    ngumUserId: PropTypes.string,
    workplace: PropTypes.string,
    workplaceData: PropTypes.shape({
      name: PropTypes.string.isRequired,
      status_id: PropTypes.number.isRequired,
    }),
  }),
  disabled: PropTypes.bool,
  vfEmailPermission: PropTypes.shape({}),
  ngumUserIdPermission: PropTypes.shape({}),
  onChange: PropTypes.func.isRequired,
};

export default React.forwardRef((props, ref) => (
  <ADConnectionListForm {...props} forwardedRef={ref} />
));

export const NGumUserIDTextFieldComponent = ({
  forwardedRef,
  data,
  readOnly,
  onChange,
}) => {
  const [value, setValue] = React.useState(data.ngumUserId || "");

  React.useEffect(() => {
    setValue(data.ngumUserId || "");
  }, [data.ngumUserId]);

  const onValueChange = (event) => {
    setValue(event.target.value);
    onChange && onChange(data.index, event.target.value);
  };

  React.useImperativeHandle(
    forwardedRef,
    () => ({
      getValue: () => value,
      setValue: (newValue) => setValue(newValue),
    }),
    [value],
  );

  return (
    <InputText
      id="ngumUserId"
      className="w3-block w3-border"
      value={value}
      onChange={onValueChange}
      readOnly={readOnly}
    />
  );
};
NGumUserIDTextFieldComponent.propTypes = {
  data: PropTypes.shape({
    index: PropTypes.number,
    ngumUserId: PropTypes.string,
  }).isRequired,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
};
export const NGumUserIDTextField = React.forwardRef((props, ref) => (
  <NGumUserIDTextFieldComponent {...props} forwardedRef={ref} />
));

const ADConnectionBottomForm = ({ model, onChange, disabled, readonly }) => {
  const style = { display: "flex", gap: "8px", alignItems: "center" };
  const vodafoneAccountAbsent = Object.keys(model).length === 0;
  const disableWorkplace =
    disabled ||
    vodafoneAccountAbsent ||
    (model.workplaceData &&
      model.workplaceData.status_id !== undefined &&
      model.workplaceData.status_id !== 0 &&
      model.workplaceData.status_id !== STATUS_IDS.FINISHED);
  console.log("ADConnectionBottomForm", model, disableWorkplace, disabled);
  return (
    <div className="w3-row neg-margin-lr">
      <div className="w3-col s12 m6 l6 pad-lr pad-big-top">
        <div className="p-col-12" style={style}>
          <Checkbox
            inputId={`msAuthentication`}
            checked={Boolean(model.isAutheticatorRegistered)}
            style={{ cursor: "default" }}
            disabled
          ></Checkbox>
          <label className="bold" htmlFor={`msAuthentication`}>
            {"Authenticator vorregistriert"}
          </label>
        </div>
      </div>
      <div className="w3-col s12 m6 l6 pad-lr pad-big-top">
        {!vodafoneAccountAbsent && (
          <div style={style}>
            <label className="bold margin-rht" htmlFor="workplace">
              {`Arbeitsort:`}
            </label>
            <div style={{ flexGrow: 1 }}>
              <WorkplaceRadioGroup
                workplace={model.workplace || ""}
                onChange={onChange}
                disabled={disableWorkplace}
                readonly={readonly}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ADConnectionPanel = ({ model, viewVFEmail, showPassword }) => {
  const data = [];
  const exists =
    model && model.adConnection && model.adConnection.toLowerCase() === "ja";
  if (exists) {
    data.push({
      ...model,
      vf_email: model.adStatus_id === STATUS_IDS.NEW ? "" : model.vf_email,
      adStatus: model.adStatus || STATUS_LABELS[model.adStatus_id],
    });
  }

  return (
    <PermissionResource resource={Resources.Profile.ADConnections}>
      <PermissionSection
        height="auto"
        id="vodafone-account"
        className="neg-margin-big-lr pad-big-lr pad-big-btm"
        isUnterWizardTitle
        idOffset={-90}
      >
        <h4 className="no-margin pad-big-top pad-btm">{"Vodafone Account"}</h4>
        <DataTable
          value={data}
          rows={1}
          emptyMessage={
            <TableEmptyMessage
              prefix="Kein"
              itemNamePlural="Vodafone Account"
              min
            />
          }
        >
          {viewVFEmail && <Column field="vf_email" header="Vodafone E-Mail" />}
          <Column field="adUsername" header="Kennung" />
          {!showPassword ? undefined : (
            <Column field="adPassword" header="Passwort" />
          )}
          <Column field="adStatus" header="Status" />
        </DataTable>
        {data.length > 0 && <ADConnectionBottomForm model={data[0]} readonly />}
      </PermissionSection>
    </PermissionResource>
  );
};
